// Billing a matter from the firm-wide screen: find the person first, by
// whatever the caller has in front of them - a name, a phone number, an
// email, an A-number, a case number - then bill the matter they pick.

import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Dialog } from "../../../components/chrome/Dialog";
import { SEARCH_MIN_QUERY, useGlobalSearch } from "../../../hooks/useGlobalSearch";
import type { MatterHit } from "../../../api/search";
import { stageLabel } from "../../../lib/caseLabels";
import { InvoiceForm } from "./InvoiceForm";

interface Props {
  open: boolean;
  onClose: () => void;
  /** What to look the matter up by when the caller already knows the
      person - an A-number or an address, not a full name: the server
      matches the name fields one at a time. */
  initialQuery?: string;
}

export function NewInvoiceDialog({ open, onClose, initialQuery }: Props) {
  const navigate = useNavigate();
  // null while the caller's own query still stands - typing takes over
  // from it, and closing hands it back.
  const [typed, setTyped] = useState<string | null>(null);
  const [picked, setPicked] = useState<MatterHit | null>(null);
  const q = typed ?? initialQuery ?? "";
  // The dialog renders nothing while closed, but this hook runs anyway.
  const search = useGlobalSearch(open ? q.trim() : "");
  const hits = search.data?.matters ?? [];

  // The write happens inside InvoiceForm, so the flag that says it is in
  // flight lives there too - the IntegrationsIndex pattern. Without this
  // a scrim click walks away from an issue that lands a second later and
  // navigates the user to a bill they thought they had cancelled.
  const issuing =
    useIsMutating({ mutationKey: ["case-invoices", picked?.caseId ?? "__idle__", "issue"] }) > 0;

  const close = () => {
    setTyped(null);
    setPicked(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} title="New invoice" locked={issuing}>
      {picked ? (
        <>
          <div className="cc-picked-row">
            <div>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                {picked.matter.clientName ?? "This matter"}
              </div>
              <div className="cc-form-hint">
                <span className="tnum-mono">{picked.matter.eID}</span>
                {picked.clientEmail ? ` ${String.fromCharCode(183)} ${picked.clientEmail}` : ""}
              </div>
            </div>
            <button type="button" className="cc-link-btn" onClick={() => setPicked(null)}>
              Change
            </button>
          </div>
          <InvoiceForm
            caseId={picked.caseId}
            clientEmail={picked.clientEmail}
            onCancel={close}
            onIssued={(invoice) => {
              close();
              navigate(`/billing/invoice/${invoice._id}`);
            }}
          />
        </>
      ) : (
        <div className="cc-form-panel">
          <label className="cc-form-field">
            <span className="cc-form-label">Find the matter</span>
            <input
              className="cc-field"
              aria-label="Find the matter"
              autoFocus
              placeholder="Name, phone, email, A-number, case number"
              value={q}
              onChange={(e) => setTyped(e.target.value)}
            />
          </label>

          {q.trim().length < SEARCH_MIN_QUERY && (
            <span className="cc-form-hint">Type at least {SEARCH_MIN_QUERY} characters.</span>
          )}
          {search.isError && (
            <span role="alert" className="cc-form-error">
              Could not reach the server - try again.
            </span>
          )}
          {q.trim().length >= SEARCH_MIN_QUERY && !search.isLoading && hits.length === 0 && (
            <span className="cc-form-hint">
              No open matter matches that. A closed matter is not billed from here.
            </span>
          )}

          <ul className="cc-pick-list">
            {hits.map((hit) => (
              <li key={hit.caseId}>
                <button type="button" className="cc-pick-row" onClick={() => setPicked(hit)}>
                  <span className="name">
                    {hit.matter.clientName ?? <span className="tnum-mono">{hit.matter.eID}</span>}
                  </span>
                  <span className="meta tnum-mono">{hit.matter.eID}</span>
                  <span className="meta">{stageLabel(hit.matter.stage)}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
}
